import React, { useState } from 'react';
import {
  Layout,
  Card,
  Typography,
  Descriptions,
  Button,
  Tag,
  Space,
  List,
  Table,
  Divider,
  Badge,
  Spin,
  Modal,
} from 'antd';
import { EditOutlined, ArrowLeftOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { generateLPReportHTML } from './PreviewModal';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const TestViewer = ({ testData, isLoading }) => {
  const navigate = useNavigate();
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  if (isLoading) {
    return (
      <Layout style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size='large' />
      </Layout>
    );
  }

  if (!testData) {
    return (
      <Layout style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text>Nenhum dado disponível</Text>
      </Layout>
    );
  }

  const handleBack = () => navigate('/settings/tests');
  
  // O ID já vem normalizado do TestBuilder
  const testId = testData.id;
  
  const handleEdit = () => {
    if (!testId) {
      return;
    }
    navigate(`/settings/tests/${testId}/edit`);
  };

  const handlePreview = () => {
    setPreviewModalVisible(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalVisible(false);
  };

  const handleDownloadPDF = () => {
    if (!testData) return;
    
    const htmlContent = generateLPReportHTML(testData);
    
    // Create a new window with the complete HTML content
    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for the content to load, then automatically trigger print dialog
    printWindow.addEventListener('load', () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    });
  };

  // Renderizar campo do formulário
  const renderFormField = (field) => (
    <div style={{ marginBottom: 16, background: '#fafafa', padding: 12, borderRadius: 4 }}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <div>
          <Text strong>{field.label}</Text>
          {field.isRequired && <Text type='danger'> *</Text>}
        </div>
        <Space>
          <Tag color={field.type === 'select' ? 'blue' : 'green'}>
            {field.type === 'select' ? 'Seleção' : 'Entrada'}
          </Tag>
          {field.type === 'select' && (
            <Tag color='orange'>
              {field.typeVariant === 'single' ? 'Única' : 'Múltipla'}
            </Tag>
          )}
          {field.isDisabled && <Tag color='default'>Desabilitado</Tag>}
        </Space>
        {field.options && field.options.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Text type='secondary'>Opções:</Text>
            <div style={{ marginTop: 4 }}>
              {field.options.map((option, idx) => (
                <Tag
                  key={idx}
                  color={field.validValue === option || (Array.isArray(field.validValue) && field.validValue.includes(option)) ? 'green' : 'default'}
                  style={{ marginBottom: 4 }}
                >
                  {option.startsWith('$') ? `Variável: ${option.slice(1)}` : option}
                  {(field.validValue === option || (Array.isArray(field.validValue) && field.validValue.includes(option))) && ' ✓'}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Space>
    </div>
  );

  // Renderizar sub-step de câmera
  const renderCameraSubStep = (subStep, index) => (
    <Card
      key={index}
      size='small'
      title={`${index + 1}. ${subStep.title}`}
      style={{ marginBottom: 8 }}
    >
      {subStep.instrumentIds && subStep.instrumentIds.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text strong>Instrumentos: </Text>
          {subStep.instrumentIds.map((inst, idx) => (
            <Tag key={idx} color='purple'>{inst}</Tag>
          ))}
        </div>
      )}
      {subStep.hints && subStep.hints.length > 0 && (
        <div>
          <Text strong>Dicas:</Text>
          <List
            size='small'
            dataSource={subStep.hints}
            renderItem={(hint) => <List.Item>{hint}</List.Item>}
          />
        </div>
      )}
      {subStep.shouldValidate && (
        <Badge status='success' text='Deve validar' style={{ marginTop: 8 }} />
      )}
    </Card>
  );

  // Renderizar step
  const renderStep = (step, index) => {
    const stepTypeMap = {
      form: { label: 'Formulário', color: 'blue' },
      camera: { label: 'Câmera', color: 'green' },
      table: { label: 'Tabela/Matriz', color: 'purple' },
    };

    const stepType = stepTypeMap[step.stepType] || { label: step.stepType, color: 'default' };

    return (
      <Card
        key={index}
        title={
          <Space>
            <Text strong>Step {index + 1}:</Text>
            <Text>{step.title}</Text>
            <Tag color={stepType.color}>{stepType.label}</Tag>
            {step.isRequired && <Tag color='red'>Obrigatório</Tag>}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {/* Campos específicos para câmera */}
        {step.stepType === 'camera' && (
          <>
            {step.durationInSeconds && (
              <Descriptions.Item label='Duração'>
                {step.durationInSeconds} segundos
              </Descriptions.Item>
            )}
            {step.intervalInSeconds && (
              <Descriptions.Item label='Intervalo'>
                {step.intervalInSeconds} segundos
              </Descriptions.Item>
            )}
            <Divider orientation='left' plain>Sub Steps</Divider>
            {step.subSteps?.map((subStep, idx) => renderCameraSubStep(subStep, idx))}
          </>
        )}

        {/* Campos para formulário */}
        {step.stepType === 'form' && step.fields && (
          <>
            <Divider orientation='left' plain>Campos</Divider>
            {step.fields.map((field, idx) => (
              <div key={idx}>{renderFormField(field)}</div>
            ))}
          </>
        )}

        {/* Configuração de tabela */}
        {step.stepType === 'table' && (
          <>
            <Descriptions bordered size='small'>
              <Descriptions.Item label='Rótulo da Linha' span={3}>
                {step.rowLabel || 'Não definido'}
              </Descriptions.Item>
            </Descriptions>
            <Divider orientation='left' plain>Estrutura da Tabela</Divider>
            <Table
              size='small'
              bordered
              pagination={false}
              columns={[
                {
                  title: step.rowLabel || 'Linhas',
                  dataIndex: 'row',
                  key: 'row',
                  width: 200,
                },
                ...(step.columns?.map((col, idx) => ({
                  title: col,
                  dataIndex: `col_${idx}`,
                  key: `col_${idx}`,
                  render: () => <Tag>Campo</Tag>,
                })) || []),
              ]}
              dataSource={step.rows?.map((row, idx) => ({
                key: idx,
                row: row,
                ...step.columns?.reduce((acc, _, colIdx) => ({
                  ...acc,
                  [`col_${colIdx}`]: '',
                }), {}),
              }))}
            />
          </>
        )}
      </Card>
    );
  };

  return (
    <Layout style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} size='large'>
          Voltar
        </Button>

        <Title level={3} style={{ margin: 0 }}>
          Visualizar Teste
        </Title>

        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={handlePreview}
            size='large'
          >
            Visualizar Relatório
          </Button>
          <Button
            type='primary'
            icon={<EditOutlined />}
            onClick={handleEdit}
            size='large'
          >
            Editar
          </Button>
        </Space>
      </Header>

      <Content
        style={{
          flex: 1,
          overflowY: 'auto',
          background: '#f5f5f5',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: '50px' }}>
          {/* Informações básicas */}
          <Card title='Informações Básicas' style={{ marginBottom: 24 }}>
            <Descriptions bordered column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
              <Descriptions.Item label='Título'>{testData.title}</Descriptions.Item>
              <Descriptions.Item label='Código'>
                <Tag color='blue'>{testData.code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label='Plataforma'>
                <Tag color={testData.platform === 'mobile' ? 'green' : 'purple'}>
                  {testData.platform === 'mobile' ? 'Mobile' : 'Desktop'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label='Nome do Relatório' span={2}>
                {testData.reportName}
              </Descriptions.Item>
              <Descriptions.Item label='Tentativas Permitidas' span={3}>
                <Badge count={testData.allowedAttempts} style={{ backgroundColor: '#52c41a' }} />
              </Descriptions.Item>
              <Descriptions.Item label='Categorias de Asset' span={3}>
                <Space wrap>
                  {testData.assetCategories?.map((cat, idx) => (
                    <Tag key={idx} color='volcano'>{cat}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label='Instrumentos' span={3}>
                <Space wrap>
                  {testData.instrumentIds?.map((inst, idx) => (
                    <Tag key={idx} color='purple'>{inst}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label='Propriedades para Exibição' span={3}>
                <Space wrap>
                  {testData.assetDisplayProperties?.map((prop, idx) => (
                    <Tag key={idx} color='blue'>{prop}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {testData.annotations && testData.annotations.length > 0 && (
              <>
                <Divider orientation='left' plain>Anotações</Divider>
                <List
                  size='small'
                  dataSource={testData.annotations}
                  renderItem={(annotation) => (
                    <List.Item>
                      <Text>{annotation}</Text>
                    </List.Item>
                  )}
                />
              </>
            )}
          </Card>

          {/* Steps */}
          <Card title='Steps do Teste'>
            {testData.steps?.map((step, idx) => renderStep(step, idx))}
          </Card>
        </div>
      </Content>

      {/* Modal de Preview do Relatório */}
      <Modal
        title="Visualizar Relatório"
        open={previewModalVisible}
        onCancel={handlePreviewClose}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>,
          <Button key="close" onClick={handlePreviewClose}>
            Fechar
          </Button>
        ]}
        width="90%"
        style={{ top: 20 }}
        styles={{ body: { padding: 0, height: '80vh', overflow: 'hidden' } }}
      >
        {testData && (
          <iframe
            srcDoc={generateLPReportHTML(testData)}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Report Preview"
          />
        )}
      </Modal>
    </Layout>
  );
};

export default TestViewer;